import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Heart, Mail, Instagram, Facebook, Linkedin } from "lucide-react";
import { NAV_LINKS, PETITION_URL, SOCIAL } from "../lib/constants";
import { usePetition } from "../lib/petitionContext";

// TikTok icon
const TikTokIcon = ({ size = 16, className = "" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
        <path d="M16.5 0H12v15.5c0 1.66-1.34 3-3 3s-3-1.34-3-3 1.34-3 3-3c.34 0 .67.06.98.16V8.05c-.32-.04-.65-.05-.98-.05C5.13 8 2 11.13 2 15s3.13 7 7 7 7-3.13 7-7V8.5c1.5 1.1 3.34 1.74 5.34 1.74V6.06c-2.83 0-4.84-1.66-4.84-6.06z" />
    </svg>
);

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { openSubscribe } = usePetition();
    const subscribeBtnRef = useRef(null);
    const mobileSubscribeBtnRef = useRef(null);

    const handleSubscribe = (ref) => (e) => {
        e.preventDefault();
        openSubscribe(ref);
        setOpen(false); // close mobile menu if open
    };

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-navbar"
            className="fixed top-0 inset-x-0 z-50 bg-editorial/95 backdrop-blur-xl border-b border-editorial-border shadow-sm"
        >
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
                <Link to="/" className="group flex items-center gap-3" data-testid="nav-logo">
                    <div className="w-10 h-10 rounded-full bg-navy-900 text-editorial flex items-center justify-center font-serif text-xl tracking-tight">
                        JC
                    </div>
                    <div className="hidden sm:block leading-tight">
                        <div className="font-serif text-xl text-navy-900">JC Law</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-navy-600/80">Foundation</div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-5 xl:gap-8">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.path}
                            to={l.path}
                            data-testid={`nav-link-${l.path.replace("/", "")}`}
                            className={({ isActive }) =>
                                `group text-sm font-medium tracking-wide whitespace-nowrap transition-colors relative py-1 ${
                                    isActive ? "text-navy-900" : "text-navy-700/80 hover:text-navy-900"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    {l.label}
                                    <span className={`absolute left-0 right-0 -bottom-0.5 h-px bg-gold origin-left transition-transform duration-300 ${isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-2 xl:gap-3">
                    <div className="hidden xl:flex items-center gap-1.5 mr-2 pr-3 border-r border-editorial-border" data-testid="nav-socials">
                        <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="nav-social-instagram" className="w-8 h-8 flex items-center justify-center text-navy-700/70 hover:text-coral transition-colors">
                            <Instagram size={16} />
                        </a>
                        <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="nav-social-facebook" className="w-8 h-8 flex items-center justify-center text-navy-700/70 hover:text-coral transition-colors">
                            <Facebook size={16} />
                        </a>
                        <a href={SOCIAL.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="nav-social-tiktok" className="w-8 h-8 flex items-center justify-center text-navy-700/70 hover:text-coral transition-colors">
                            <TikTokIcon />
                        </a>
                        <a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="nav-social-linkedin" className="w-8 h-8 flex items-center justify-center text-navy-700/70 hover:text-coral transition-colors">
                            <Linkedin size={16} />
                        </a>
                    </div>
                    <a
                        href={PETITION_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-petition-btn"
                        className="text-sm font-medium px-4 py-2.5 border border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-editorial transition-all duration-300 whitespace-nowrap"
                    >
                        Sign Petition
                    </a>
                    <button
                        ref={subscribeBtnRef}
                        type="button"
                        onClick={handleSubscribe(subscribeBtnRef)}
                        data-testid="nav-subscribe-btn"
                        className="text-sm font-medium px-4 py-2.5 border border-navy-900/30 text-navy-900 hover:border-navy-900 hover:bg-navy-900/5 transition-all duration-300 flex items-center gap-1.5 whitespace-nowrap"
                    >
                        <Mail className="w-4 h-4" />
                        Subscribe
                    </button>
                    <Link
                        to="/donate"
                        data-testid="nav-donate-btn"
                        className="text-sm font-semibold px-4 py-2.5 bg-coral text-white hover:bg-coral-dark transition-all duration-300 flex items-center gap-1.5 whitespace-nowrap"
                    >
                        <Heart className="w-4 h-4" />
                        Donate
                    </Link>
                </div>

                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    className="lg:hidden p-2 text-navy-900"
                    aria-label={open ? "Close menu" : "Open menu"}
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X size={26} /> : <Menu size={26} />}
                </button>
            </div>

            {/* Mobile menu */}
            <div
                className={`lg:hidden overflow-hidden transition-all duration-500 ${
                    open ? "max-h-[800px] border-t border-editorial-border" : "max-h-0"
                }`}
                data-testid="mobile-menu"
            >
                <div className="px-6 py-6 flex flex-col gap-1 bg-editorial">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.path}
                            to={l.path}
                            data-testid={`mobile-nav-link-${l.path.replace("/", "")}`}
                            className={({ isActive }) =>
                                `py-3 text-base font-medium border-b border-editorial-border ${
                                    isActive ? "text-coral" : "text-navy-800"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <div className="flex flex-col gap-3 pt-5">
                        <a
                            href={PETITION_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-center text-sm font-medium px-5 py-3 border border-navy-900 text-navy-900"
                        >
                            Sign the Petition
                        </a>
                        <button
                            ref={mobileSubscribeBtnRef}
                            type="button"
                            onClick={handleSubscribe(mobileSubscribeBtnRef)}
                            data-testid="mobile-nav-subscribe-btn"
                            className="text-center text-sm font-medium px-5 py-3 border border-navy-900/30 text-navy-900 flex items-center justify-center gap-2"
                        >
                            <Mail className="w-4 h-4" />
                            Subscribe
                        </button>
                        <Link
                            to="/donate"
                            className="text-center text-sm font-semibold px-5 py-3 bg-coral text-white"
                        >
                            Donate
                        </Link>
                    </div>
                    <div className="flex items-center justify-center gap-4 pt-6 pb-2" data-testid="mobile-socials">
                        <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 border border-editorial-border rounded-full flex items-center justify-center text-navy-700 hover:text-coral hover:border-coral transition-colors">
                            <Instagram size={18} />
                        </a>
                        <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 border border-editorial-border rounded-full flex items-center justify-center text-navy-700 hover:text-coral hover:border-coral transition-colors">
                            <Facebook size={18} />
                        </a>
                        <a href={SOCIAL.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="w-10 h-10 border border-editorial-border rounded-full flex items-center justify-center text-navy-700 hover:text-coral hover:border-coral transition-colors">
                            <TikTokIcon size={18} />
                        </a>
                        <a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-10 h-10 border border-editorial-border rounded-full flex items-center justify-center text-navy-700 hover:text-coral hover:border-coral transition-colors">
                            <Linkedin size={18} />
                        </a>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Navbar;
